import * as THREE from 'three';
import { gsap } from 'gsap';
import { services } from './serviceData';

// Helper function to create remote control rectangle
const createRemoteControl = (scene, onButtonClick, onHelpClick, camera) => {
  // Load remote control texture
  const textureLoader = new THREE.TextureLoader();
  const remoteTexture = textureLoader.load('./remote-control.svg');
  
  // Create rectangle geometry - responsive sizing for mobile landscape
  const isMobile = window.innerWidth <= 900;
  const isLandscape = window.innerWidth > window.innerHeight;
  
  // Increase size for mobile landscape orientation
  let width = 1.5;
  let height = 3;
  
  if (isMobile && isLandscape) {
    width = 2.2;  // Increase width by ~47%
    height = 4.4; // Increase height by ~47%
  }
  
  const rectangleGeometry = new THREE.PlaneGeometry(width, height);
  
  // Create material with remote texture - light gray color
  const remoteMaterial = new THREE.MeshStandardMaterial({
    map: remoteTexture,
    transparent: true,
    alphaTest: 0.1,         // Remove black background
    color: 0x999999,        // Slightly lighter gray color
    emissive: 0x505050,     // Subtle lighter emissive
    emissiveMap: remoteTexture, // Use the same texture for emissive
    emissiveIntensity: 0.3  // Moderate glow intensity
  });
  
  // Create remote control mesh
  const remoteMesh = new THREE.Mesh(rectangleGeometry, remoteMaterial);
  
  // Add resize listener to update remote size on orientation change
  const handleResize = () => {
    const newIsMobile = window.innerWidth <= 900;
    const newIsLandscape = window.innerWidth > window.innerHeight;
    
    let newWidth = 1.5;
    let newHeight = 3;
    
    if (newIsMobile && newIsLandscape) {
      newWidth = 2.2;
      newHeight = 4.4;
    }
    
    // Update geometry if size changed
    if (remoteMesh.geometry.parameters.width !== newWidth || remoteMesh.geometry.parameters.height !== newHeight) {
      remoteMesh.geometry.dispose();
      remoteMesh.geometry = new THREE.PlaneGeometry(newWidth, newHeight);
    }
  };
  
  window.addEventListener('resize', handleResize);
  window.addEventListener('orientationchange', handleResize);
  
  // Position at center bottom of screen - fixed position relative to camera
  remoteMesh.position.set(0, -1.0, -5); // Moved slightly down as requested
  remoteMesh.rotation.x = 0.4; // More tilt for angled appearance like second image
  remoteMesh.rotation.y = 0.1; // Slight Y rotation for perspective
  remoteMesh.rotation.z = 0.05; // Slight Z rotation for natural look
  
  // Make the remote follow the camera (always visible at bottom center)
  const updateRemotePosition = () => {
    if (camera) {
      // Get camera's forward direction
      const cameraDirection = new THREE.Vector3();
      camera.getWorldDirection(cameraDirection);
      
      // Position remote at bottom center of screen, relative to camera
      const remotePosition = camera.position.clone();
      remotePosition.add(cameraDirection.multiplyScalar(3)); // 3 units in front (closer for better visibility)
      remotePosition.y = camera.position.y - 1.0; // Moved slightly down as requested
      
      remoteMesh.position.copy(remotePosition);
      remoteMesh.lookAt(camera.position);
      remoteMesh.rotation.x += 0.4; // Maintain angled tilt
      remoteMesh.rotation.y += 0.1; // Maintain Y rotation
      remoteMesh.rotation.z += 0.05; // Maintain Z rotation
    }
    requestAnimationFrame(updateRemotePosition);
  };
  updateRemotePosition();
  
  // Add a spotlight specifically to illuminate the remote
  const remoteSpotlight = new THREE.SpotLight(0xffffff, 2, 10, Math.PI / 4, 0.5, 1);
  remoteSpotlight.position.set(0, 0, 2);
  remoteSpotlight.target = remoteMesh;
  scene.add(remoteSpotlight);
  
  // Update spotlight position to follow remote
  const updateSpotlightPosition = () => {
    if (camera && remoteMesh) {
      remoteSpotlight.position.copy(camera.position);
      remoteSpotlight.position.y += 2;
      remoteSpotlight.position.z += 2;
    }
    requestAnimationFrame(updateSpotlightPosition);
  };
  updateSpotlightPosition();
  
  scene.add(remoteMesh);
  
  // Add click detection for remote control buttons
  const setupButtonDetection = () => {
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    
    // Helper function to check if coordinates are within a button area
    const isInButtonArea = (svgX, svgY) => {
      return (
        (svgX >= 120 && svgX <= 280 && svgY >= 500 && svgY <= 540) || // PROMO VIDEOS
        (svgX >= 120 && svgX <= 280 && svgY >= 560 && svgY <= 600) || // CORPORATE VIDEOS
        (svgX >= 120 && svgX <= 280 && svgY >= 620 && svgY <= 660) || // MUSIC VIDEOS
        (svgX >= 120 && svgX <= 280 && svgY >= 680 && svgY <= 720) || // FILM PRODUCTION
        (svgX >= 255 && svgX <= 305 && svgY >= 175 && svgY <= 225)    // HELP BUTTON
      );
    };
    
    const onRemoteMouseMove = (event) => {
      // Calculate mouse position
      mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
      mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
      
      // Update the picking ray
      raycaster.setFromCamera(mouse, camera);
      
      // Check if remote control is hovered
      const intersects = raycaster.intersectObject(remoteMesh);
      const canvas = document.querySelector('canvas');
      
      if (intersects.length > 0 && canvas) {
        const intersection = intersects[0];
        const uv = intersection.uv;
        
        // Convert UV coordinates to SVG coordinates (400x800 viewBox)
        const svgX = uv.x * 400;
        const svgY = (1 - uv.y) * 800; // Flip Y coordinate
        
        // Change cursor based on whether we're over a button
        if (isInButtonArea(svgX, svgY)) {
          canvas.style.cursor = 'pointer';
        } else {
          canvas.style.cursor = 'default';
        }
      } else if (canvas) {
        canvas.style.cursor = 'default';
      }
    };
    
    const onRemoteClick = (event) => {
      // Calculate mouse position
      mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
      mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
      
      // Update the picking ray
       raycaster.setFromCamera(mouse, camera);
      
      // Check if remote control is clicked
      const intersects = raycaster.intersectObject(remoteMesh);
      
      if (intersects.length > 0) {
        const intersection = intersects[0];
        const uv = intersection.uv;
        
        // Convert UV coordinates to SVG coordinates (400x800 viewBox)
        const svgX = uv.x * 400;
        const svgY = (1 - uv.y) * 800; // Flip Y coordinate
        
        // Check which button was clicked based on SVG coordinates
        // PROMO VIDEOS button: x=120-280, y=500-540
        if (svgX >= 120 && svgX <= 280 && svgY >= 500 && svgY <= 540) {
          const promoService = services.find(s => s.id === 'promo');
          if (promoService && onButtonClick) {
            onButtonClick(promoService);
          }
        }
        // CORPORATE VIDEOS button: x=120-280, y=560-600
        else if (svgX >= 120 && svgX <= 280 && svgY >= 560 && svgY <= 600) {
          const corporateService = services.find(s => s.id === 'corporate');
          if (corporateService && onButtonClick) {
            onButtonClick(corporateService);
          }
        }
        // MUSIC VIDEOS button: x=120-280, y=620-660
        else if (svgX >= 120 && svgX <= 280 && svgY >= 620 && svgY <= 660) {
          const musicService = services.find(s => s.id === 'music');
          if (musicService && onButtonClick) {
            onButtonClick(musicService);
          }
        }
        // FILM PRODUCTION button: x=120-280, y=680-720
        else if (svgX >= 120 && svgX <= 280 && svgY >= 680 && svgY <= 720) {
          const filmService = services.find(s => s.id === 'film');
          if (filmService && onButtonClick) {
            onButtonClick(filmService);
          }
        }
        // HELP button: x=255-305, y=175-225
        else if (svgX >= 255 && svgX <= 305 && svgY >= 175 && svgY <= 225) {
          if (onHelpClick) {
            onHelpClick();
          }
        }
      }
    };
    
    // Add event listeners to canvas
    const canvas = document.querySelector('canvas');
    if (canvas) {
      canvas.addEventListener('click', onRemoteClick);
      canvas.addEventListener('mousemove', onRemoteMouseMove);
      
      // Return cleanup function
      return () => {
        canvas.removeEventListener('click', onRemoteClick);
        canvas.removeEventListener('mousemove', onRemoteMouseMove);
      };
    }
    
    return () => {};
  };
  
  // Setup button detection
  const cleanupButtonDetection = setupButtonDetection();
  
  // Store cleanup function on the mesh for later use
  const cleanupResize = () => {
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('orientationchange', handleResize);
  };
  
  remoteMesh.userData.cleanup = () => {
    cleanupButtonDetection();
    cleanupResize();
  };
  
  return remoteMesh;
};

export const createServiceNodes = async (scene, services, onButtonClick, onHelpClick, camera, cinematicMode = false) => {
  // Create remote control rectangle
  const remoteControl = createRemoteControl(scene, onButtonClick, onHelpClick, camera);
  
  // Set remote visibility based on cinematic mode
  remoteControl.visible = !cinematicMode;
  remoteControl.name = 'remoteMesh'; // Add name for easy reference
  
  // Add subtle floating animation to remote control (keeping it fixed in place)
  gsap.to(remoteControl.rotation, {
    z: remoteControl.rotation.z + 0.05,
    duration: 3,
    repeat: -1,
    yoyo: true,
    ease: "sine.inOut"
  });
  
  // Return the remote control as the only service node
  return [remoteControl];
};